import { Injectable } from '@angular/core';
import { Claim_Document } from '../models/claim_document.model';
import { DocumentService } from './base/document.service';
import { v4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class ClaimDocumentService extends DocumentService<Claim_Document> {
  constructor() {
    super('cp_claim_document');
    // route create/update through lambdas
    this.lambdaNames = { create: 'create_document', update: 'update_document' };
  }

  public async upload(
    f: File,
    claim_id: string,
    name: string,
    bucket: string,
    seq?: number,
    question_id?: string
  ) {
    const rawExt = f.name.split('.').pop();
    const ext = rawExt && rawExt !== f.name ? rawExt : undefined;

    const id = v4();

    console.log('Uploading document', { f, claim_id, name, bucket, seq, question_id });
    // Helper: sanitize a provided name to be filesystem/storage-safe
    const sanitize = (s: string) =>
      s
        .toString()
        .normalize('NFKD')
        .replace(/[^\w\s.-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase()
        .slice(0, 120);

    // If a name was provided, prefer it as the base filename; otherwise use the original file name (sans ext)
    const providedBase = name ? sanitize(name) : undefined;
    const origBase = f.name ? f.name.replace(/\.[^.]+$/, '') : 'file';
    const base = providedBase || sanitize(origBase) || id;

    // short random token to avoid collisions
    const token = v4().split('-')[0];

    // Build URL: if seq provided, group under a folder for this document (useful for multi-page uploads)
    // Examples:
    //  - no seq: <claim_id>/<base>-<token>.<ext>
    //  - with seq: <claim_id>/<base>-<token>/<seq>.<ext>
    const fileBase = `${base}-${token}`;
    const filename = seq === undefined ? `${fileBase}${ext ? `.${ext}` : ''}` : `${seq}${ext ? `.${ext}` : ''}`;
    const url = seq === undefined ? `${claim_id}/${filename}` : `${claim_id}/${fileBase}/${filename}`;
    const pd: Claim_Document = {
      bucket,
      // Prefer the file MIME type for doc_type, fall back to the provided name
      doc_type: f.type || name,
      // Use provided human-readable name if present, otherwise original filename
      doc_name: name || f.name,
      kv_extract: {},
      ocr_extract: {},
      status: 'PRE_UPLOAD',
      url,
      claim_id: claim_id,
      question_id: question_id,
      created_at: new Date(),
      id,
    };

    const ulret = await this.uploadDocument(pd, f);

    if (ulret.error || !ulret.data) {
      return { data: null, error: ulret.error };
    }

    return {
      data: {
        doc: pd,
        path: ulret.data.path,
      },
      error: null,
    };
  }
}
